import { inject } from "@angular/core";
import { LgFilterSet } from "@logex/framework/lg-filterset";
import {
    AppVersionSettings,
    argsExcept,
    FilterFactory,
    FilterFactoryCreator,
    Filters,
    getFilterIds
} from "@shared";
import { AppDefinitions } from "@shared/app-definitions.service";
import { ScenarioSettingsService } from "@shared/scenario/scenario-settings-service";
import { Observable, take } from "rxjs";
import { LoaderArgumentsMap } from "../load-manager";
import { IVariationFilterProvider } from "./variation-filter-provider.types";

export abstract class VariationFiltersProvider implements IVariationFilterProvider {
    private _scenarioSettings: AppVersionSettings;

    readonly defaultReferencesParams: { [key: string]: string | number } = {
        firstReferenceId: 4,
        secondReferenceId: 0,
        weightType: 1
    };

    protected _filterFactory = inject(FilterFactory);
    protected _appDefinition = inject(AppDefinitions);

    constructor() {
        inject(ScenarioSettingsService)
            .scenarioSettings.pipe(take(1))
            .subscribe(settings => {
                this._scenarioSettings = settings;
            });
    }

    _getLocalizedFilters(): {
        [x: string]: {
            name: string;
            options: (value: number | string | { from: number; to: number }) => string;
        };
    } {
        const localizedFilters = {
            [Filters.IS_NEW]: {
                name: "APP._Filters.IsNew",
                options: (value: number | string) => {
                    return +value === 0 ? "APP._Filters.IsNew__no" : "APP._Filters.IsNew__yes";
                }
            },
            [Filters.IS_CHRONIC]: {
                name: "APP._Filters.IsChronic",
                options: (value: number | string) => {
                    return +value === 0
                        ? "APP._Filters.IsChronic__no"
                        : "APP._Filters.IsChronic__yes";
                }
            },
            [Filters.COMORBIDITY_INDEX]: {
                name: "APP._Filters.IsComorbid",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("comorbidityIndex", value);
                }
            },
            [Filters.IS_FRAIL_ELDERLY]: {
                name: "APP._Filters.IsFrailElderly",
                options: (value: number | string) => {
                    return +value === 0
                        ? "APP._Filters.IsFrailElderly__no"
                        : "APP._Filters.IsFrailElderly__yes";
                }
            },
            [Filters.GENDER]: {
                name: "APP._Filters.Gender",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("gender", value);
                }
            },
            [Filters.AGE_CATEGORY]: {
                name: "APP._Filters.AgeCategory",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("ageCategory", value);
                }
            },
            [Filters.MUNICIPALITY]: {
                name: "APP._Filters.Municipality",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("municipality", value);
                }
            },
            [Filters.HOSPITAL_AREA]: {
                name: "APP._Filters.HospitalArea",
                options: (value: number | string) => {
                    return +value === 0
                        ? "APP._Filters.HospitalArea_no"
                        : "APP._Filters.HospitalArea_yes";
                }
            },
            [Filters.SOCIAL_STATUS]: {
                name: "APP._Filters.SocialStatus",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("socialStatus", value);
                }
            },
            [Filters.INSURER]: {
                name: "APP._Filters.Insurer",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("insurer", value);
                }
            },
            [Filters.CCS0]: {
                name: "APP._Filters.Ccs0",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("ccs0", value);
                }
            },
            [Filters.DIAGNOSIS_GROUP]: {
                name: "FW._Filters.DiagnosisGroup",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("diagnosisGroup", value);
                }
            },
            [Filters.DIAGNOSIS]: {
                name: "FW._Filters.Diagnosis",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("diagnosis", value);
                }
            },
            [Filters.SPECIALISM]: {
                name: "FW._Filters.Specialism",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("specialism", value);
                }
            },
            [Filters.RVE]: {
                name: "APP._Filters.Rve",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("rve", value);
                }
            },
            [Filters.TRC1]: {
                name: "APP._Filters.Trc1",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("trc1", value);
                }
            },
            [Filters.TRC2]: {
                name: "APP._Filters.Trc2",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("trc2", value);
                }
            },
            [Filters.TRC3]: {
                name: "APP._Filters.Trc3",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("trc3", value);
                }
            },
            [Filters.CARE_CLUSTER]: {
                name: "APP._Filters.CareCluster",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("careCluster", value);
                }
            },
            [Filters.URGENCY_CARE]: {
                name: "APP._Filters.UrgencyCare",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("urgencyCare", value);
                }
            },
            [Filters.PANDEMIC_CARE]: {
                name: "APP._Filters.PandemicCare",
                options: (value: number | string) => {
                    const definition = {
                        0: "APP._Filters.PandemicCare__duringCorona",
                        1: "APP._Filters.PandemicCare__preCorona",
                        2: "APP._Filters.PandemicCare__postCorona"
                    };
                    return definition[+value];
                }
            },
            [Filters.FLAG]: {
                name: "APP._Filters.Flag",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("flag", value);
                }
            },
            [Filters.DIFF_COST]: {
                name: "APP._Filters.DiffCost",
                options: (value: { from: number; to: number }) => {
                    return ` from ${value.from || "-∞"} to ${value.to || "∞"}`;
                }
            },
            [Filters.PATIENT_VOLUME]: {
                name: "APP._Filters.PatientVolume",
                options: (value: { from: number; to: number }) => {
                    return ` from ${value.from || "-∞"} to ${value.to || "∞"}`;
                }
            },
            [Filters.IS_REFERRED]: {
                name: "APP._Filters.IsReferred",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("referral", value);
                }
            }
        };
        return localizedFilters;
    }

    public get pagekey(): string {
        return "";
    }

    public init(
        source: (filterName: string, argsOverride?: LoaderArgumentsMap) => Observable<any>,
        extParams: _.Dictionary<any>
    ): FilterFactoryCreator<{}, {}> {
        const creator = this._configureFilters(this._filterFactory, source);

        for (const key in extParams) {
            if (Object.prototype.hasOwnProperty.call(extParams, key)) {
                const filterDef = creator.getFilter(key);
                if (filterDef) {
                    Object.assign(filterDef, extParams[key]);
                }
            }
        }
        return creator;
    }

    protected _configureFilters(
        filterFactory: FilterFactory,
        source: (filterName: string) => Observable<any>
    ): FilterFactoryCreator<any, {}> {
        const filterFactoryCreator = filterFactory.define();
        this._definePatientFeatures(filterFactoryCreator, source);
        this._definePatientTypes(filterFactoryCreator, source);
        this._defineCareQuestionsGroup(filterFactoryCreator, source);
        this._defineTreatmentChoices(filterFactoryCreator, source);
        this._defineSpecificFiltersGroups(filterFactoryCreator, source);
        this._defineCostDifferenceFilters(filterFactoryCreator);
        filterFactoryCreator.flagsFilter({ source: () => source(Filters.FLAG) });
        this._defineSpecialism(filterFactoryCreator, source);
        filterFactoryCreator.patientVolumeFilter({ nameLC: "APP._Filters.PatientsVolume" });
        filterFactoryCreator.rveFilter({
            source: () => source(Filters.RVE),
            visible: () =>
                !!this._scenarioSettings && this._scenarioSettings.is_rve_filtering_available
        });
        return filterFactoryCreator;
    }

    protected abstract _defineSpecificFiltersGroups(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    );

    protected _defineCostDifferenceFilters(filterFactoryCreator: FilterFactoryCreator<any>): void {
        filterFactoryCreator
            .startGroup("APP._FilterGroups.CostDifference")
            .rangeMoneyFilter(Filters.DIFF_COST, {
                nameLC: "APP._Filters.DiffCost"
            });
    }

    protected _definePatientTypes(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ) {
        filterFactoryCreator
            .startGroup("APP._FilterGroups.PatientType")
            .isNewFilter({ source: () => source(Filters.IS_NEW) })
            .isReferredFilter({ source: () => source(Filters.IS_REFERRED) })
            .isChronicFilter({ source: () => source(Filters.IS_CHRONIC) })
            .isComorbidFilter({
                source: () => source(Filters.COMORBIDITY_INDEX)
            })
            .isFrailElderlyFilter({
                source: () => source(Filters.IS_FRAIL_ELDERLY)
            })
            .referrerTypeFilter({
                source: () => source(Filters.REFERRER_TYPE),
                visible: () => false
            })
            .pandemicCareFilter({ source: () => source(Filters.PANDEMIC_CARE) });
        // .isUrgencyCareFilter( { source: () => this._applySource( source, Filters.URGENCY_CARE ) } );
    }

    protected _definePatientFeatures(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ) {
        filterFactoryCreator
            .startGroup("APP._FilterGroups.PatientFeature")
            .genderFilter({ source: () => source(Filters.GENDER) })
            .ageCategoryFilter({ source: () => source(Filters.AGE_CATEGORY) })
            .municipalityFilter({ source: () => source(Filters.MUNICIPALITY) })
            .hospitalAreaFilter({
                source: () => source(Filters.HOSPITAL_AREA),
                visible: () =>
                    !!this._scenarioSettings && this._scenarioSettings.is_servicearea_available
            })
            .socialStatusFilter({
                source: () => source(Filters.SOCIAL_STATUS)
            })
            .insurerFilter({
                source: () => source(Filters.INSURER)
            })
            .locationFilter({ source: () => source(Filters.LOCATION) });
    }

    protected _defineCareQuestionsGroup(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ) {
        filterFactoryCreator
            .startGroup("APP._FilterGroups.CareQuestion")
            .ccs0Filter({ source: () => source(Filters.CCS0) })
            .diagnosisGroupFilter({
                source: () => source(Filters.DIAGNOSIS_GROUP)
            })
            .diagnosisFilter({ source: () => source(Filters.DIAGNOSIS) })
            .careClusterFilter({
                source: () => source(Filters.CARE_CLUSTER)
            });
    }

    protected _defineTreatmentChoices(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ) {
        filterFactoryCreator
            .startGroup("APP._FilterGroups.TreatmentChoice")
            .trc1Filter({ source: () => source(Filters.TRC1) })
            .trc2Filter({ source: () => source(Filters.TRC2) })
            .trc3Filter({ source: () => source(Filters.TRC3) });
    }

    protected _defineSpecialism(
        filterFactoryCreator: FilterFactoryCreator<any>,
        source: (filterName: string) => Observable<any>
    ) {
        filterFactoryCreator.startGroup().specialismFilter({
            source: () => source(Filters.SPECIALISM)
        });
    }

    _getLoaderArguments(filterContext: LgFilterSet<any, any>): LoaderArgumentsMap {
        return {
            [Filters.AGE_CATEGORY]: () => getFilterIds(Filters.AGE_CATEGORY, filterContext),
            [Filters.CCS0]: () => getFilterIds(Filters.CCS0, filterContext),
            [Filters.DIAGNOSIS_GROUP]: () => getFilterIds(Filters.DIAGNOSIS_GROUP, filterContext),
            [Filters.DIAGNOSIS]: () => getFilterIds(Filters.DIAGNOSIS, filterContext),
            [Filters.GENDER]: () => getFilterIds(Filters.GENDER, filterContext),
            [Filters.INSURER]: () => getFilterIds(Filters.INSURER, filterContext),
            [Filters.IS_CHRONIC]: () => getFilterIds(Filters.IS_CHRONIC, filterContext),
            [Filters.COMORBIDITY_INDEX]: () =>
                getFilterIds(Filters.COMORBIDITY_INDEX, filterContext),
            [Filters.IS_FRAIL_ELDERLY]: () => getFilterIds(Filters.IS_FRAIL_ELDERLY, filterContext),
            [Filters.IS_NEW]: () => getFilterIds(Filters.IS_NEW, filterContext),
            [Filters.MUNICIPALITY]: () => getFilterIds(Filters.MUNICIPALITY, filterContext),
            [Filters.CARE_CLUSTER]: () => getFilterIds(Filters.CARE_CLUSTER, filterContext),
            [Filters.REFERRER_TYPE]: () => getFilterIds(Filters.REFERRER_TYPE, filterContext),
            [Filters.SOCIAL_STATUS]: () => getFilterIds(Filters.SOCIAL_STATUS, filterContext),
            [Filters.SPECIALISM]: () => getFilterIds(Filters.SPECIALISM, filterContext),
            [Filters.TRC1]: () => getFilterIds(Filters.TRC1, filterContext),
            [Filters.TRC2]: () => getFilterIds(Filters.TRC2, filterContext),
            [Filters.TRC3]: () => getFilterIds(Filters.TRC3, filterContext),
            [Filters.RVE]: () => getFilterIds(Filters.RVE, filterContext),
            [Filters.HOSPITAL_AREA]: () => getFilterIds(Filters.HOSPITAL_AREA, filterContext),
            [Filters.PANDEMIC_CARE]: () => getFilterIds(Filters.PANDEMIC_CARE, filterContext),
            [Filters.FLAG]: () => getFilterIds(Filters.FLAG, filterContext),
            [Filters.IS_REFERRED]: () => getFilterIds(Filters.IS_REFERRED, filterContext),
            [Filters.LOCATION]: () => getFilterIds(Filters.LOCATION, filterContext)
        } as LoaderArgumentsMap;
    }

    _createLoaders(
        args: LoaderArgumentsMap,
        action: (filterName: string, args: LoaderArgumentsMap) => any
    ) {
        return {
            ...action(Filters.AGE_CATEGORY, argsExcept(args, Filters.AGE_CATEGORY)),
            ...action(Filters.CCS0, argsExcept(args, Filters.CCS0)),
            ...action(Filters.DIAGNOSIS_GROUP, argsExcept(args, Filters.DIAGNOSIS_GROUP)),
            ...action(Filters.DIAGNOSIS, argsExcept(args, Filters.DIAGNOSIS)),
            ...action(Filters.GENDER, argsExcept(args, Filters.GENDER)),
            ...action(Filters.INSURER, argsExcept(args, Filters.INSURER)),
            ...action(Filters.LOCATION, argsExcept(args, Filters.LOCATION)),
            ...action(Filters.IS_CHRONIC, argsExcept(args, Filters.IS_CHRONIC)),
            ...action(Filters.COMORBIDITY_INDEX, argsExcept(args, Filters.COMORBIDITY_INDEX)),
            ...action(Filters.IS_FRAIL_ELDERLY, argsExcept(args, Filters.IS_FRAIL_ELDERLY)),
            ...action(Filters.IS_NEW, argsExcept(args, Filters.IS_NEW)),
            ...action(Filters.MUNICIPALITY, argsExcept(args, Filters.MUNICIPALITY)),
            ...action(Filters.CARE_CLUSTER, argsExcept(args, Filters.CARE_CLUSTER)),
            ...action(Filters.REFERRER_TYPE, argsExcept(args, Filters.REFERRER_TYPE)),
            ...action(Filters.SOCIAL_STATUS, argsExcept(args, Filters.SOCIAL_STATUS)),
            ...action(Filters.SPECIALISM, argsExcept(args, Filters.SPECIALISM)),
            ...action(Filters.TRC1, argsExcept(args, Filters.TRC1)),
            ...action(Filters.TRC2, argsExcept(args, Filters.TRC2)),
            ...action(Filters.TRC3, argsExcept(args, Filters.TRC3)),
            ...action(Filters.RVE, argsExcept(args, Filters.RVE)),
            ...action(Filters.PANDEMIC_CARE, argsExcept(args, Filters.PANDEMIC_CARE)),
            ...action(Filters.HOSPITAL_AREA, argsExcept(args, Filters.HOSPITAL_AREA)),
            // ...action( Filters.URGENCY_CARE, argsExcept( args, Filters.URGENCY_CARE ) ),
            ...action(Filters.FLAG, argsExcept(args, Filters.FLAG)),
            ...action(Filters.IS_REFERRED, argsExcept(args, Filters.IS_REFERRED))
        };
    }

    protected _filterOptionsLoaderName(filterName: string) {
        return `${filterName}Filter`;
    }
}
